import { useState, useEffect, ChangeEvent } from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useRecoilState } from "recoil";

import { SettingsContainer } from "../../../../../components/molecules/settingsContainer";
import { Error } from "../../../../../components/atoms/form/error";
import { Button } from "../../../../../components/atoms/form/button";
import { AccountViewmodel } from "../../ViewModel";
import { useUser } from "../../../../Hooks/useUser";
import { useManageSubscription } from "MVVM/Hooks/useManageSubscription";
import { GdprDeletePending } from "MVVM/State";
import { Divider } from "MVVM/Components/Divider";
import { analytics } from "MVVM/Analytics";

type ActiveFlow = "deactivate" | "gdprDelete" | null;

interface DeactivateAccountState {
  error: boolean;
  errorMsg: string;
  activeFlow: ActiveFlow;
  subscriptionBlocked: boolean;
}

const DeleteAccount = () => {
  const {
    userDelete,
    deleteLoading,
    deleteError,
    deleteData,
    userGdprDelete,
    gdprDeleteData,
    gdprDeleteLoading,
    gdprDeleteError,
  } = AccountViewmodel();
  const { logout } = useUser();
  const { handleManageSubscription, isActiveSubscriber } =
    useManageSubscription();
  const [gdprDeletePending, setGdprDeletePending] =
    useRecoilState(GdprDeletePending);
  const [deletionReason, setDeletionReason] = useState("");

  const isLoading = deleteLoading || gdprDeleteLoading;

  const [state, setState] = useState<DeactivateAccountState>({
    error: false,
    errorMsg: "",
    activeFlow: null,
    subscriptionBlocked: false,
  });

  useEffect(() => {
    if (deleteError) {
      setState((prev) => ({
        ...prev,
        error: true,
        errorMsg: deleteError.message || "Error deactivating account.",
      }));
    }
  }, [deleteError]);

  useEffect(() => {
    if (gdprDeleteError) {
      setState((prev) => ({
        ...prev,
        error: true,
        errorMsg:
          gdprDeleteError.message || "Error submitting deletion request.",
      }));
    }
  }, [gdprDeleteError]);

  useEffect(() => {
    if (deleteData?.userDelete?.success) {
      analytics.track("Account Deactivated", {
        reason: deletionReason || "No reason provided",
      });
      logout("ACCOUNT_DEACTIVATED");
      window.location.href = "/";
    }
  }, [deleteData, deletionReason, logout]);

  useEffect(() => {
    if (gdprDeleteData?.userGdprDelete?.success) {
      analytics.track("Account Permanent Delete Requested", {
        reason: deletionReason || "No reason provided",
      });
      setGdprDeletePending(true);
      setState((prev) => ({ ...prev, activeFlow: null }));
    } else if (gdprDeleteData?.userGdprDelete?.errors?.length) {
      setState((prev) => ({
        ...prev,
        error: true,
        errorMsg:
          gdprDeleteData.userGdprDelete.errors[0].message ||
          "Error submitting deletion request.",
      }));
    }
  }, [gdprDeleteData, deletionReason, setGdprDeletePending]);

  const initiateFlow = (flow: "deactivate" | "gdprDelete") => {
    setState((prev) => ({
      ...prev,
      activeFlow: flow,
      subscriptionBlocked: isActiveSubscriber,
      error: false,
      errorMsg: "",
    }));
  };

  const handleConfirm = async () => {
    try {
      if (state.activeFlow === "gdprDelete") {
        await userGdprDelete();
      } else {
        await userDelete();
      }
    } catch {
      setState((prev) => ({
        ...prev,
        error: true,
        errorMsg:
          state.activeFlow === "gdprDelete"
            ? "Error submitting deletion request."
            : "Error deactivating account.",
        activeFlow: null,
      }));
    }
  };

  const handleCancel = () => {
    setState({
      error: false,
      errorMsg: "",
      activeFlow: null,
      subscriptionBlocked: false,
    });
    setDeletionReason("");
  };

  if (gdprDeletePending) {
    return (
      <StyledSettingsContainer mt={6} mb={5} title="Account Management">
        <Divider />
        <Box pb={5}>
          <Text>
            Your permanent account deletion request has been submitted and is
            being processed. This may take some time to complete.
          </Text>
        </Box>
      </StyledSettingsContainer>
    );
  }

  return (
    <StyledSettingsContainer mt={6} mb={5} title="Account Management">
      <Divider />
      <Box pb={3}>
        <Text>
          Deactivating will make your account inactive but save your data if you
          want to come back. If you'd like to permanently delete your account
          and all your data instead,{" "}
          <PermanentDeleteLink onClick={() => initiateFlow("gdprDelete")}>
            click here
          </PermanentDeleteLink>
          .
        </Text>
        <Text mt={4} mb={2} color="chalk" fontSize={2}>
          Your profile will no longer be reachable, your clips will be hidden,
          and new clips requested for you by partners will not be made.
        </Text>
      </Box>

      {state.error && <Error>{state.errorMsg}</Error>}

      {!state.activeFlow ? (
        <>
          <Flex pt={[5]} justifyContent="flex-start">
            <Button
              size="small"
              backgroundColor="darkerNed"
              style={{ border: "1px solid #41525cfc" }}
              onClick={() => initiateFlow("deactivate")}
              disabled={isLoading}
              flex={true}
            >
              Deactivate Account
            </Button>
          </Flex>
        </>
      ) : (
        <ConfirmBox>
          <Text mb={4} color="error" fontSize={3} fontWeight={500}>
            Are you absolutely sure?{" "}
            {state.activeFlow === "gdprDelete" && (
              <Text fontWeight={900} style={{ display: "inline" }}>
                This action cannot be undone.
              </Text>
            )}
          </Text>
          {state.subscriptionBlocked && (
            <SubscriptionWarningBox mb={4}>
              <Text fontSize={2} color="alert">
                Heads up — you might still have an active subscription. You may
                want to cancel it first to avoid future charges! You can{" "}
                <PermanentDeleteLink
                  onClick={() => handleManageSubscription("Account Management")}
                >
                  manage your subscription here.
                </PermanentDeleteLink>
              </Text>
            </SubscriptionWarningBox>
          )}
          {state.activeFlow === "gdprDelete" && (
            <Text mb={4} color="chalk" fontSize={2}>
              This will permanently delete all your data. This includes all of
              your clips on Allstar.gg and any clips viewable on partner sites.
              This request may take some time to process.
            </Text>
          )}
          <Box mb={4} mt={6}>
            <Text mb={2} fontSize={2} color="ice" fontWeight={500}>
              Reason for{" "}
              {state.activeFlow === "gdprDelete" ? "deleting" : "deactivating"}{" "}
              your account (required):
            </Text>
            <StyledTextarea
              value={deletionReason}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setDeletionReason(e.target.value)
              }
              placeholder="e.g. Not using the platform anymore, privacy concerns, etc."
              disabled={isLoading}
              rows={3}
            />
          </Box>
          <Flex pt={[3]} justifyContent="flex-start">
            <Button
              size="small"
              backgroundColor="alert"
              onClick={handleConfirm}
              disabled={isLoading || !deletionReason.trim()}
              flex={true}
            >
              {isLoading
                ? state.activeFlow === "gdprDelete"
                  ? "Submitting..."
                  : "Deactivating..."
                : state.activeFlow === "gdprDelete"
                ? "Yes, Permanently Delete Account"
                : "Yes, Deactivate Account"}
            </Button>
            <Button
              type="button"
              size="small"
              ml={4}
              onClick={handleCancel}
              disabled={isLoading}
              flex={true}
            >
              Cancel
            </Button>
          </Flex>
        </ConfirmBox>
      )}
    </StyledSettingsContainer>
  );
};

const ConfirmBox = styled(Box)`
  background: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 20px;
  padding: 20px;
`;

const StyledSettingsContainer = styled(SettingsContainer)`
  ${(props) => {
    if (props.isPopup) {
      return `
      background: none;
      border: none;
      padding:0px;`;
    }
  }}
`;

const SubscriptionWarningBox = styled(Box)`
  background: ${({ theme }) => theme.colors.flashWarning};
  border: 1px solid ${({ theme }) => theme.colors.errorOutline};
  border-radius: 12px;
  font-weight: 700;
  padding: 16px;
`;

const PermanentDeleteLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-size: inherit;
  padding: 0;
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const StyledTextarea = styled.textarea`
  background-color: ${({ theme }) => theme.colors.ned};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.ned};
  color: ${({ theme }) => theme.colors.ice};
  font-family: inherit;
  font-size: 16px;
  max-width: 400px;
  min-height: 80px;
  padding: 12px;
  resize: vertical;
  width: 100%;

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.chalk};
  }
`;

export { DeleteAccount };
